import React, { useContext, useState, useRef } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  IconButton,
  Link,
  Paper,
  Switch,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import { UserContext } from "../../context/UserContext";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import AddIcon from "@mui/icons-material/Add";
import Images from "../../request/components/Images";
import { PRODUCT_TYPES_V2, QUOTE_TAGS, STORAGE_BUCKET } from "../../common/constants";
import { Autocomplete } from "@mui/material";
import currency from "currency.js";
import {
  getAmountWithTxnFee,
  getItemBasePrice,
  getItemPrice,
  getItemSurchargeTotal,
  getProductTypeVariants,
  getVariantsBySize,
  isSalesTaxEnabled,
  isSalesTaxDefaultEnabled,
  pruneVariantFieldsOnSizeChange,
  applyPhotoVariantPricing,
  syncItemImagesWithPhotoVariant,
  syncPhotoVariantWithItemImages,
} from "../../common/util";
import TextWithReadMore from "../../common/TextWithReadMore";
import QuillRTE from "../../common/component/QuillRTE";
import VariantSelection from "./VariantSelection";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import DoneIcon from "@mui/icons-material/Done";
import VariantSelectionCategory from "./VariantSelectionCategory";
import QuantityPicker from "../../common/component/QuantityPicker";
import ButtonWithConfirmation from "../../common/ButtonWithConfirmation";
import TotalSurchargeLabel from "../../common/component/lineitem/TotalSurchargeLabel";
import Price from "../../common/component/Price";
import AddCustomType from "./AddCustomType";
import AddExtraCharge from "./AddExtraCharge";
import Option, { OPTION_GRID_TILE_SX } from "./Option";
import OptionGridCell from "./OptionGridCell";
import { ExpandMoreOutlined } from "@mui/icons-material";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";

export default function CustomItem(props) {
  const { user } = useContext(UserContext);
  const { immutablePrice } = props;
  const errorRef = useRef();

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [item, setItem] = useState(props.item || "");
  const [errors, setErrors] = useState([]);

  const [tab, setTab] = useState("options");
  const [open, setOpen] = useState(false);

  const hasBasePrice = getItemBasePrice(item?.variant) > 0;

  const validate = () => {
    let arr = [];
    if (!item.type?.trim()) {
      arr.push("Item Type is required");
    }
    if (item.type === "Other" && !item.title?.trim()) {
      arr.push("Title is required");
    }
    if (!(+item.price > 0)) {
      arr.push(hasBasePrice ? "Selected size must have a price" : "Item Price is required");
    }
    if (!(+item.quantity > 0)) {
      arr.push("Quantity is required");
    }

    return arr;
  };

  const handleSubmit = () => {
    const arr = validate();
    setErrors(arr);

    if (arr.length > 0) {
      errorRef.current?.scrollIntoView({ behavior: "smooth" });

      return;
    } else {
      const cartItem = isSalesTaxEnabled(user.config)
        ? {
            ...item,
            salesTax: {
              enabled:
                item?.salesTax?.enabled !== undefined
                  ? `${item.salesTax.enabled}` === "true"
                  : isSalesTaxDefaultEnabled(user.config),
            },
          }
        : item;
      props.onChange(cartItem);
      handleClose();
    }
  };

  const handleDelete = () => {
    props.onChange("", "delete");
    handleClose();
  };

  const handleClose = () => {
    setOpen(false);
    setItem("");
    setTab("options");
    setErrors([]);
  };

  const updateTotal = ({ price, includeFees }, variant, quantity, images) => {
    const baseFromSize = getItemBasePrice(variant);
    if (baseFromSize > 0) {
      const priceWithSurcharge = getItemPrice(variant, baseFromSize);
      const total = currency(priceWithSurcharge).multiply(quantity).value;
      setItem({
        ...item,
        variant,
        price: baseFromSize,
        quantity,
        total,
        ...(images ? { images } : {}),
      });
      return;
    }

    const priceWithFees = includeFees ? getAmountWithTxnFee(price, user.config?.fees) : price;
    const surcharge = getItemSurchargeTotal(variant, price);
    const total = currency(priceWithFees).add(surcharge).multiply(quantity).value;

    setItem({
      ...item,
      variant,
      price,
      quantity,
      includeFees,
      total,
      ...(images ? { images } : {}),
    });
  };

  const handleItemTypeChange = (type) => {
    let title = "";
    if (type !== "Other") {
      title = type;
    }
    setItem({
      images: item.images,
      title,
      type,
      price: "",
      quantity: 1,
      includeFees: item.includeFees,
      salesTax: item.salesTax,
    });
  };

  let variants = (item.type && getProductTypeVariants(user.config, item.type)) || {};
  let tmp = getVariantsBySize(variants, item);

  const optionFields = Object.entries(tmp).filter(
    ([k, v]) => v.field.type === "select" || v.field.type === "photo"
  );

  const openNewCustomItem = () => {
    setItem({
      images: [],
      title: "CUSTOM ITEM",
      type: "",
      price: "",
      quantity: 1,
      includeFees: user.config?.transferFees === "false" ? false : true,
      // No product flag — default to bakery sales-tax default; baker can override on the line.
      salesTax: { enabled: isSalesTaxDefaultEnabled(user.config) },
    });
    setOpen(true);
  };

  return (
    <>
      {props.item ? (
        <Link
          color="inherit"
          underline="never"
          sx={{ cursor: "pointer" }}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();

            setItem(props.item);
            setOpen(true);
          }}
        >
          {props.children}
        </Link>
      ) : (
        <Button
          type="button"
          variant="outlined"
          startIcon={<AddIcon />}
          onClick={(e) => {
            e.stopPropagation();
            openNewCustomItem();
          }}
          sx={{
            alignSelf: "stretch",
            width: "100%",
            height: "auto",
            minHeight: 56,
            minWidth: 0,
            flexShrink: 1,
            px: 2,
            borderColor: "#78374a",
            color: "#78374a",
            fontWeight: 600,
            textTransform: "none",
            whiteSpace: "nowrap",
            "&:hover": {
              borderColor: "#5c2a3a",
              bgcolor: "rgba(120, 55, 74, 0.06)",
            },
          }}
        >
          CUSTOM ITEM
        </Button>
      )}
      {open && (
        <Dialog fullScreen={true} open={open} fullWidth={true}>
          <form
            onSubmit={handleSubmit}
            style={{
              display: "contents",
            }}
          >
            <DialogTitle
              sx={{
                display: "flex",
                gap: "10px",
                alignItems: "flex-start",
                margin: "0",
                backgroundColor: "#F7F7F7",
                borderBottom: "1px solid #DDD",
                flexWrap: "wrap",
                padding: isMobile ? "8px 8px 16px" : "16px 24px 16px",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <IconButton onClick={handleClose}>
                  <ArrowBackIcon />
                </IconButton>
                <Box>
                  <Typography variant="h5">Custom Item</Typography>
                </Box>
              </Box>
              <Box
                sx={{
                  display: "flex",
                  alignItems: hasBasePrice ? "center" : "flex-start",
                  justifyContent: "right",
                  flexGrow: "1",
                  gap: "5px",
                }}
              >
                {hasBasePrice ? (
                  <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                    <Box sx={{ fontSize: "1.4rem", fontWeight: "700" }}>
                      {currency(item.price || 0).format()}
                    </Box>
                    <TotalSurchargeLabel item={item} />
                  </Box>
                ) : (
                  <Box sx={{ maxWidth: "12rem" }}>
                    <Price
                      label={"Item Price"}
                      value={{ amount: item.price, includeFees: item.includeFees }}
                      onChange={(o) => {
                        updateTotal(
                          { price: o.amount, includeFees: o.includeFees },
                          item.variant,
                          item.quantity
                        );
                      }}
                    />
                    <TotalSurchargeLabel item={item} />
                  </Box>
                )}
                <QuantityPicker
                  width={hasBasePrice ? undefined : "120px"}
                  disabled={immutablePrice}
                  value={item.quantity}
                  onChange={(quantity) => {
                    updateTotal(
                      { price: item.price, includeFees: item.includeFees },
                      item.variant,
                      quantity
                    );
                  }}
                />
                <Button
                  fullWidth
                  color="primary"
                  variant="contained"
                  onClick={handleSubmit}
                  sx={{
                    height: "56px",
                    whiteSpace: "nowrap",
                    width: hasBasePrice ? "10rem" : undefined,
                    maxWidth: "12rem",
                  }}
                  endIcon={<DoneIcon />}
                  disabled={hasBasePrice && +(item?.total || "") <= 0}
                >
                  {hasBasePrice
                    ? currency(item.total || 0).format()
                    : +item.total > 0
                      ? currency(item.total).format()
                      : "Save"}
                </Button>
              </Box>
            </DialogTitle>
            <DialogContent sx={{ backgroundColor: "#F7F7F7", padding: "0" }}>
              <Box ref={errorRef}>
                {errors.length > 0 && (
                  <Alert sx={{ justifyContent: "center" }} severity="error">
                    {errors.join(". ")}
                  </Alert>
                )}
              </Box>
              <Box px={2} py={2}>
                <Grid container>
                  <Grid item sm={4}></Grid>
                  <Grid item sm={8}>
                    <Box>
                      <Tabs
                        value={tab}
                        variant="scrollable"
                        scrollButtons="auto"
                        allowScrollButtonsMobile
                      >
                        <Tab value="options" label={"Options"} onClick={() => setTab("options")} />
                        <Tab value="notes" label={"Notes"} onClick={() => setTab("notes")} />
                      </Tabs>
                    </Box>
                  </Grid>
                  <Grid item sm={4}>
                    <Box pr={4} sx={{ position: "-webkit-sticky", position: "sticky", top: "0" }}>
                      <Images
                        edit={true}
                        pics={item.images}
                        onChange={(images) => {
                          const nextVariant = syncPhotoVariantWithItemImages(
                            images,
                            item.images,
                            item.variant,
                            tmp
                          );
                          updateTotal(
                            { price: item.price, includeFees: item.includeFees },
                            nextVariant,
                            item.quantity,
                            syncItemImagesWithPhotoVariant(
                              images,
                              item.variant,
                              nextVariant,
                              tmp
                            )
                          );
                        }}
                        folder={`tmp`}
                      />
                    </Box>
                  </Grid>
                  <Grid item sm={8}>
                    {tab === "options" && (
                      <Box>
                        <VariantSelectionCategory
                          field={{ name: "Item Type", required: true }}
                          cat={"type"}
                          variant={{ name: item.type, value: item.type }}
                        >
                          <Box>
                            <Grid container spacing={2} sx={{ alignItems: "stretch" }}>
                              {(
                                (user.config?.catalog && Object.values(user.config?.catalog)) ||
                                PRODUCT_TYPES_V2
                              )
                                .sort((o1, o2) => o1.name.localeCompare(o2.name))
                                .map((o, idx) => {
                                  if (o.name === "Other") {
                                    return null;
                                  } else {
                                    return (
                                      <OptionGridCell
                                        gridKey={`custom-type-${o.name ?? "type"}-${idx}`}
                                      >
                                        <Option
                                          title={o.name}
                                          description={""}
                                          img={`${STORAGE_BUCKET}${o.img}`}
                                          sx={OPTION_GRID_TILE_SX}
                                          onClick={(selected) => handleItemTypeChange(o.name)}
                                          selected={item.type === o.name}
                                        />
                                      </OptionGridCell>
                                    );
                                  }
                                })}
                              {item.type &&
                              !(Object.values(user.config?.catalog) || PRODUCT_TYPES_V2).find(
                                (o) => o.name === item.type
                              ) ? (
                                <OptionGridCell gridKey="custom-type-selected">
                                  <Option
                                    title={item.type}
                                    description={``}
                                    sx={OPTION_GRID_TILE_SX}
                                    onClick={(selected) => handleItemTypeChange("")}
                                  />
                                </OptionGridCell>
                              ) : (
                                <OptionGridCell gridKey="custom-type-other">
                                  <AddCustomType
                                    label={`Add Custom Option`}
                                    onChange={(o) => {
                                      handleItemTypeChange(o.name);
                                    }}
                                  >
                                    <Option
                                      title={`Other`}
                                      img={`${STORAGE_BUCKET}/img/icon/outline/menu.png`}
                                      description={`Enter Custom Item Type`}
                                      sx={OPTION_GRID_TILE_SX}
                                      onClick={(selected) => {}}
                                    />
                                  </AddCustomType>
                                </OptionGridCell>
                              )}
                            </Grid>
                          </Box>
                        </VariantSelectionCategory>
                        <Box>
                          <VariantSelection
                            basePrice={
                              hasBasePrice ? getItemBasePrice(item.variant) : item.price
                            }
                            variant={item.variant || ""}
                            options={optionFields}
                            onChange={(variant) => {
                              const pruned = pruneVariantFieldsOnSizeChange(
                                variant,
                                item.variant || {},
                                tmp
                              );
                              const synced = applyPhotoVariantPricing(pruned, tmp);
                              updateTotal(
                                { price: item.price, includeFees: item.includeFees },
                                synced,
                                item.quantity,
                                syncItemImagesWithPhotoVariant(
                                  item.images,
                                  item.variant,
                                  synced,
                                  tmp
                                )
                              );
                            }}
                          />
                          {hasBasePrice && (
                            <AddExtraCharge
                              variant={item.variant || {}}
                              onChange={(variant) =>
                                updateTotal(
                                  { price: item.price, includeFees: item.includeFees },
                                  variant,
                                  item.quantity
                                )
                              }
                            />
                          )}
                        </Box>
                        {isSalesTaxEnabled(user.config) && (
                          <Box sx={{ marginTop: "15px", marginBottom: "15px" }}>
                            <Accordion elevation={0} defaultExpanded={false}>
                              <AccordionSummary expandIcon={<ExpandMoreOutlined />}>
                                <Box style={{ display: "flex", alignItems: "center", gap: "7px" }}>
                                  <SettingsOutlinedIcon
                                    style={{ fontSize: "1.3rem", color: "#777" }}
                                  />
                                  <Typography variant="subtitle1">Settings</Typography>
                                </Box>
                              </AccordionSummary>
                              <AccordionDetails>
                                <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                                  Override whether this custom item is taxable on the current cart.
                                </Typography>
                                <FormControlLabel
                                  control={
                                      <Switch
                                        checked={
                                          item?.salesTax?.enabled !== undefined
                                            ? `${item.salesTax.enabled}` === "true"
                                            : isSalesTaxDefaultEnabled(user.config)
                                        }
                                        onChange={(e) =>
                                          setItem({
                                            ...item,
                                            salesTax: { enabled: e.target.checked },
                                          })
                                        }
                                      />
                                  }
                                  label="Apply sales tax to this item"
                                />
                              </AccordionDetails>
                            </Accordion>
                          </Box>
                        )}
                        <Box my={2}>
                          <Button
                            size="large"
                            fullWidth
                            variant="contained"
                            color="secondary"
                            onClick={() => setTab("notes")}
                          >
                            CONTINUE
                          </Button>
                        </Box>
                      </Box>
                    )}
                    {tab === "notes" && (
                      <Box>
                        <Paper elevation={0} sx={{ minHeight: "100%" }}>
                          <Box px={2} py={4}>
                            {item.type === "Cake" && (
                              <Box mb={3}>
                                <TextField
                                  id="inscription"
                                  value={item?.variant?.inscription?.name}
                                  label="Inscription"
                                  name="inscription"
                                  variant="outlined"
                                  fullWidth
                                  onChange={(e) =>
                                    setItem({
                                      ...item,
                                      variant: {
                                        ...item.variant,
                                        inscription: { name: e.target.value },
                                      },
                                    })
                                  }
                                />
                              </Box>
                            )}
                            <Box mb={3}>
                              <Autocomplete
                                value={item.tags || []}
                                onChange={(e, tags) => setItem({ ...item, tags })}
                                options={QUOTE_TAGS}
                                filterSelectedOptions
                                renderInput={(params) => (
                                  <TextField variant="outlined" {...params} label="Tags" />
                                )}
                                multiple
                                freeSolo
                              />
                            </Box>
                            <QuillRTE
                              value={item.description || ""}
                              onChange={(description) => setItem({ ...item, description })}
                            />
                            <Box py={0.4}>
                              <TextWithReadMore
                                data={`These notes are shown to customer. ${
                                  item.type === "Cake"
                                    ? `Use it to describe decor (edible image, ruffles, sugar flowers..), toppers (edible 3D
                      fondant, 2D cardstock) & more. Propose alternative options when sending quotes.`
                                    : ""
                                }`}
                                limit={140}
                              />
                            </Box>
                          </Box>
                        </Paper>
                        <Box my={2}>
                          <Button
                            size="large"
                            fullWidth
                            variant="contained"
                            color="secondary"
                            onClick={handleSubmit}
                          >
                            SAVE
                          </Button>
                        </Box>
                      </Box>
                    )}
                    {props.item && (
                      <Box my={4} textAlign={"center"}>
                        <ButtonWithConfirmation
                          copy="Are you sure you want to delete this item?"
                          onConfirm={handleDelete}
                        >
                          <Button color="error">Delete This Item</Button>
                        </ButtonWithConfirmation>
                      </Box>
                    )}
                  </Grid>
                </Grid>
              </Box>
              <Box py={20}></Box>
            </DialogContent>
          </form>
        </Dialog>
      )}
    </>
  );
}
